<template>
    <div>
        <DCard
            padding
        >
            <DRow class="pb-2">
                <DCol>
                    <DTag
                        color="primary"
                        :label="selectedItems + ' ' + __('customers.invoices.selected_items_for_cancellation')"
                    />
                </DCol>
            </DRow>
            <DRow
                class="mt-3 mb-3"
                align-vertical="end"
            >
                <DCol
                    sm="5"
                    class="pt-1"
                >
                    <DInput
                        id="search"
                        v-model="invoiceData.search"
                        :label="__('messages.search')"
                        :placeholder="__('customers.invoices.search_placeholder')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="6"
                    class="d-flex gap-2"
                >
                    <DButton
                        :label="__('messages.actions.filter')"
                        @click="filter()"
                    />
                    <DButton
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters()"
                    />
                </DCol>
            </DRow>
            <DRow>
                <DCol>
                    <DTable
                        :items="invoiceData.items || []"
                        :fields="fields"
                        :header="true"
                    >
                        <template #checkbox="{item}">
                            <td>
                                <DFormCheck
                                    v-if="item.id"
                                    :id="`invoice_item_${item.id}`"
                                    :custom="true"
                                    :checked="form.invoiceItemIds.includes(item.id)"
                                    @click="updateCheckedItems(item)"
                                />
                            </td>
                        </template>
                        <template #tax_rate="{item}">
                            <td>{{ item.tax_rate ? item.tax_rate + '%' : '' }}</td>
                        </template>
                        <template #price="{item}">
                            <td>{{ item.price ? priceFormatForDisplay(item.price, 2, customer.market_region_id) : '' }}</td>
                        </template>
                        <template #net_amount="{item}">
                            <td>{{ item.net_amount ? priceFormatForDisplay(item.net_amount, 2, customer.market_region_id) : '' }}</td>
                        </template>
                    </DTable>
                    <DPagination
                        v-if="invoiceData.last_page > 1"
                        :active-page="invoiceData.current_page"
                        :pages="invoiceData.last_page"
                        align="center"
                        @update:activePage="changePage"
                    />
                </DCol>
            </DRow>
            <DFabs>
                <DFab
                    icon="close"
                    color="danger"
                    :disabled="!form.invoiceItemIds.length"
                    :label="__('customers.invoices.items.cancel_items')"
                    @click="showCancelInvoiceItemModal()"
                />
            </DFabs>
            <DModal
                v-model="form.activity_log_comment"
                v-model:show="showConfirmationModal"
                :title="__('customers.invoices.items.confirm_title')"
                is-confirm
                :is-awaiting-validation="true"
                :show-change-reason-input="true"
                is-error
                is-big
                :show-incident-list="true"
                :customer-id="customer?.id ?? 0"
                @setIncident="setIncident"
                @close="showConfirmationModal = false"
                @confirm="cancelCallBack"
            >
                <DCard padding>
                    <DRow>
                        <DCol>
                            <DAlert color="warning">
                                {{ __('customers.invoices.items.confirm_message') }}
                            </DAlert>
                        </DCol>
                    </DRow>
                    <DRow>
                        <DCol>
                            <DTable
                                :items="itemsCancellationPreview"
                                :fields="previewFields"
                                :header="true"
                            >
                                <template #total_amount="{item}">
                                    <td>{{ item.total_amount ? priceFormatForDisplay(item.total_amount, 2, customer.market_region_id) : '' }}</td>
                                </template>
                            </DTable>
                        </DCol>
                    </DRow>
                </DCard>
            </DModal>
        </DCard>
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'

import priceFormat from '~@/Mixins/priceFormat'

export default {
    name: 'InvoiceItemIndex',
    mixins: [
        priceFormat
    ],
    layout: [App, CustomerBase],
    props: {
        invoice: {
            type: Object,
            default () {
                return {}
            }
        },
        customer: {
            type: Object,
            default () {
                return {}
            }
        }
    },
    data () {
        return {
            fields: [
                { key: 'checkbox', label: '' },
                { key: 'quantity', label: this.__('customers.invoices.form.quantity') },
                { key: 'line_item', label: this.__('customers.invoices.form.line_item') },
                { key: 'description', label: this.__('customers.invoices.form.description') },
                { key: 'tax_rate', label: this.__('customers.invoices.tax_rate') },
                { key: 'from_date', label: this.__('customers.invoices.form.from_date') },
                { key: 'to_date', label: this.__('customers.invoices.form.to_date') },
                { key: 'price', label: this.__('customers.invoices.form.price') },
                { key: 'net_amount', label: this.__('customers.invoices.net_amount') }
            ],
            previewFields: [
                { key: 'id', label: this.__('customers.invoices.form.id') },
                { key: 'quantity', label: this.__('customers.invoices.form.quantity') },
                { key: 'line_item', label: this.__('customers.invoices.form.line_item') },
                { key: 'description', label: this.__('customers.invoices.form.description') },
                { key: 'net_amount', label: this.__('customers.invoices.net_amount') }
            ],
            form: this.$inertia.form({
                invoiceItemIds: [],
                activity_log_comment: '',
                loadedFirstTime: true,
                incident_id: null
            }),
            showConfirmationModal: false,
            cancelCallBack: () => { return {} },
            invoiceData: this.invoice,
            itemsCancellationPreview: []
        }
    },
    computed: {
        selectedItems () {
            return this.form.invoiceItemIds.length
        }
    },
    methods: {
        updateCheckedItems (invoiceItem) {
            const itemIndex = this.form.invoiceItemIds.indexOf(invoiceItem.id)
            if (itemIndex < 0) {
                this.form.invoiceItemIds.push(invoiceItem.id)
                this.itemsCancellationPreview.push(invoiceItem)
                return
            }
            this.form.invoiceItemIds.splice(itemIndex, 1)
            this.itemsCancellationPreview.splice(itemIndex, 1)
        },
        showCancelInvoiceItemModal () {
            this.cancelCallBack = () => {
                this.form.post(this.$route('customers.customers.invoices.items-cancel', {
                    customer: this.customer.id,
                    invoice: this.invoice.id
                }), {}, {
                    onSuccess: () => {
                        this.showConfirmationModal = false
                    }
                })
            }
            this.showConfirmationModal = true
        },
        changePage (selectedPage) {
            const params = {
                customer: this.customer.id,
                invoice: this.invoice.id,
                page: selectedPage,
                search: this.invoiceData.search
            }

            window.axios.get(this.$route('customers.customers.invoices.items', params))
                .then(response => {
                    this.invoiceData = response.data
                })
        },
        filter () {
            this.changePage(this.invoiceData.current_page)
        },
        resetFilters () {
            this.invoiceData.search = null
            this.changePage(this.invoiceData.current_page)
        },
        setIncident (value) {
            this.form.incident_id = value
        }
    }
}
</script>
